import React, { useEffect, useState } from 'react';
import { useHelpStore } from '../Help/HelpStore';
import { IndeterminateCircleLoader } from 'cdk-radial';
import styled from 'styled-components';
import '../Walkme/walkme-modal2.css';
import { useSideSheet } from '../../contexts/SideSheet';
import { useUserProfile } from '../../hooks/queries';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import { useTheme } from '../../contexts/Theme';

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

function CenteredSpinner() {
  return (
    <SpinnerContainer>
      <IndeterminateCircleLoader hideLabel label="" />
    </SpinnerContainer>
  );
}

export const loadDynamicWalkmeScript = (application: string, walkmeScript: string) => {
  if (application !== '') {
    let script: any = document.querySelector(`script[src="${walkmeScript}"]`);
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/javascript');
      script.setAttribute('src', walkmeScript);
      script.setAttribute('class', 'walkme-to-remove');
      script.async = true;
      script.defer = false;
      document.head.appendChild(script);
      window._walkmeConfig = { smartLoad: true };
    }
  }
};


const LoadWalkme = React.memo(() => {
  const { currentAppInView, walkmeScript } = useHelpStore(
    (state: any) => state
  );
  const { trackEvent } = useAnalytics();
  const [walkmeUILoaded, setWalkmeUILoaded] = useState(true);
  const { setSideSheetContent } = useSideSheet();
  const { data } = useUserProfile();
  const { darkMode } = useTheme();

  const changeLanguage = () => {
    let userLocale = data?.locale || '';
    userLocale = userLocale.replace('_', '-');
    userLocale = userLocale === 'en-US' ? '' : userLocale;
    window.WalkMeAPI.changeLanguage(userLocale);
  };

  const handleHelpLink = (topic: string) => {
    trackEvent(AMPLITUDE_EVENTS.OPEN_HELP_TOPIC, { 'Help topic': topic });
  };

  useEffect(() => {
      if (window.WalkMeAPI) {
        changeLanguage();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Track all event listeners to prevent memory leaks
    const clickListeners = new Map<Element, Function[]>();
    
    loadDynamicWalkmeScript(currentAppInView, walkmeScript);

    window.walkme_ready = function (eventData: any) {
      if (window.WalkMePlayerAPI) {
        if (currentAppInView !== 'unify') {
          window.WalkMePlayerAPI.toggleMenu();
        }
      }
    };

    window.walkme_event = function (eventData: any) {
      if (eventData.Type === 'MenuShow') {
        const menu = document.getElementById('walkme-menu');
        if (menu) {
          document.getElementById('help-cdk-walkme-menu')?.appendChild(menu);
        }
        setWalkmeUILoaded(false);
      } else if (eventData.Type === 'MenuBuilt') {
        if(darkMode) {
              document.querySelectorAll(".walkme-name").forEach((el:any) => {
                  el.style.setProperty("color", "white", "important");
                });
                const box = document.querySelector(".walkme-search-box") as any;
                box?.style?.setProperty("color","white","important")
        }

        setTimeout(() => {
          // Clear existing listeners before adding new ones
          clickListeners.forEach((listeners, el) => {
            listeners.forEach(listener => {
              el.removeEventListener('click', listener as any);
            });
          });
          clickListeners.clear();

          document
            .querySelectorAll('.walkme-deployable-row')
            .forEach(function (el) {
              const helpLinkListener = function(e : any) {
                handleHelpLink(e.target.parentElement.innerText);
              };
              const sideSheetListener = function (e: any) {
                if (currentAppInView !== 'unify') {
                  setSideSheetContent(null);
                }
              };
              el.addEventListener('click', helpLinkListener);
              el.addEventListener('click', sideSheetListener);
              
              // Track listeners for cleanup
              if (!clickListeners.has(el)) {
                clickListeners.set(el, []);
              }
              clickListeners.get(el)?.push(helpLinkListener, sideSheetListener);
            });
        }, 100);
      } else if (eventData.Type === 'afterRemoveWalkMe') {
        const pattern = /^wm-/;
        Object.keys(localStorage).forEach((key) => {
          if (pattern.test(key)) {
            localStorage.removeItem(key);
          }
        });
      }
    };

    const resetExistingWalkmeInstance = async () => {
      const menu = document.getElementById('walkme-menu');
      if (!menu) {
        window.WalkMePlayerAPI?.toggleMenu();
      }
      if (menu) {
        const menu = document.getElementById('walkme-menu');
        if (menu && menu.children.length > 0) {
          if (currentAppInView === 'unify') {
            window.WalkMePlayerAPI?.hideMenu();
            window.WalkMePlayerAPI?.toggleMenu();
          } else {
            document.getElementById('help-cdk-walkme-menu')?.appendChild(menu);
            setWalkmeUILoaded(false);
          }
        }
      }
    };

    resetExistingWalkmeInstance();

    return () => {
      // Clean up all event listeners
      clickListeners.forEach((listeners, el) => {
        listeners.forEach(listener => {
          el.removeEventListener('click', listener as any);
        });
      });
      clickListeners.clear();
      
      window.walkme_ready = undefined;
      window.walkme_event = undefined;
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAppInView,walkmeScript]);

  return <>{walkmeUILoaded && <CenteredSpinner />}</>;
});

const DynamicHelp = React.memo(() => {
  return (
    <div>
      {<LoadWalkme />}
      <div id="help-cdk-walkme-menu" style={{ position: 'inherit' }}></div>
    </div>
  );
});

export default DynamicHelp;
